/**
 * Where each workflow stands, read cheaply from what the IDE already loaded:
 * the file tree (unsorted files, files still named as uploaded) and the workspace (drafts,
 * forms, packet, research and their own statuses), plus the outcome of the
 * last run this browser made. Pure so the rules are testable.
 */
import type { FsNode } from "../../caseFiles/types";
import type {
  WorkspaceItem,
  WorkspaceSection
} from "../workspace/workspaceModel";
import { isOptional, WORKFLOWS } from "./catalog";
import {
  applyPacketOrder,
  DEFAULT_PACKET_CONTENTS,
  DEFAULT_PACKET_ORDER,
  packetPartsOf,
  type PacketContentKind,
  type PacketContents,
  type PacketPart
} from "./packetContents";
import { formsView, packetForms } from "./formsStatus";
import { holdText, type WorkflowHold } from "./holds";
import { researchView, type ResearchTopicsState } from "./researchTopics";
import type {
  FileRef,
  LetterType,
  WorkflowId,
  WorkflowRunRecord,
  WorkflowStatus,
  WorkflowStepState,
  WorkflowView
} from "./types";

export type WorkflowFacts = {
  files: number;
  /** Files at the top level or left in the Unsorted folder. */
  unsorted: number;
  folders: number;
  /** Every file, in tree order. */
  allFiles: FileRef[];
  /** Files still named as uploaded (see `stillUploadName`). */
  untitled: FileRef[];
  /** When each file was added, to tell which arrived after the last organize. */
  addedAt: string[];
  /** Every file with when it was added and last changed. */
  stamps: FileStamp[];
  /** Files at the top level or left in the Unsorted folder. */
  unsortedFiles: FileRef[];
  /** Some of `unsortedFiles` sit in the Unsorted folder, not at the top level. */
  inUnsortedFolder: boolean;
  research: WorkspaceItem[];
  letters: WorkspaceItem[];
  forms: WorkspaceItem[];
  /** The case's exhibit list drafts (the workspace's Exhibit List). */
  exhibitLists: WorkspaceItem[];
  packet: WorkspaceItem | null;
  /** Work products Assemble Filing Packet can include, plus the exhibits row. */
  packetParts: PacketPart[];
  /** Hand picks on this case; missing keys follow `packetContents`. */
  packetPicks: Record<string, boolean>;
  /** Drag order of packet part keys; empty keeps the default kind order. */
  packetOrder: string[];
  /** The firm's include defaults for a new packet. */
  packetContents: PacketContents;
  /** The firm's order of work product kinds for a new packet. */
  packetKindOrder: readonly PacketContentKind[];
  /** The case's label style: "Numbers (1., 1.1.)". */
  labelStyle: string;
  /**
   * The case has its exhibit style. False only while it still has
   * CaseBlink's default and the firm saved a different one to apply.
   */
  styleSettled: boolean;
  /** Case Research's topics; null reads the research memos instead. */
  researchTopics: ResearchTopicsState | null;
  /** The title template and firm title instructions, as `titleSettingsKey`. */
  titleSettings: string;
  /** The letters Draft Letters writes; null while they load. */
  letterTypes: LetterType[] | null;
};

/** What the tree and workspace can't say: the case's label style, its research topics. */
export type CaseFacts = Pick<WorkflowFacts, "labelStyle" | "styleSettled"> &
  Partial<
    Pick<
      WorkflowFacts,
      | "researchTopics"
      | "titleSettings"
      | "letterTypes"
      | "packetPicks"
      | "packetContents"
      | "packetKindOrder"
      | "packetOrder"
    >
  >;

/** A short, stable hash, to compare without storing every title. */
const hash = (text: string): string => {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1)
    // eslint-disable-next-line no-bitwise
    h = (h * 33 + text.charCodeAt(i)) | 0;
  // eslint-disable-next-line no-bitwise
  return (h >>> 0).toString(36);
};

/** Every file's id and name: a file added, renamed or removed changes it. */
export const titlesKey = (files: readonly FileRef[]): string =>
  hash(
    files
      .map((f) => `${f.id}\u0000${f.name}`)
      .sort()
      .join("\u0001")
  );

export const titleSettingsKey = (s: {
  titleTemplate: string;
  titleInstructions: string | null;
}): string => hash(`${s.titleTemplate}\u0000${s.titleInstructions ?? ""}`);

/**
 * Retitle's last run covered every file with today's title settings, and no
 * file was added or renamed since. A run that renamed nothing while files
 * kept their upload names covered nothing: its renames failed.
 */
export const retitleCurrent = (
  facts: Pick<
    WorkflowFacts,
    "files" | "allFiles" | "titleSettings" | "untitled"
  >,
  record: WorkflowRunRecord | undefined
): boolean =>
  facts.files > 0 &&
  record?.status === "done" &&
  !!record.covered &&
  record.covered.settings === facts.titleSettings &&
  record.covered.titles === titlesKey(facts.allFiles) &&
  (record.covered.before !== record.covered.titles ||
    facts.untitled.length === 0);

/** A file with when it was added and last changed. */
export type FileStamp = FileRef & {
  addedAt: string | null;
  changedAt: string | null;
  /** `organizer` after an applied Organize run; those files are not new uploads. */
  placedBy?: string | null;
};

const NO_CASE_FACTS: CaseFacts = {
  labelStyle: "",
  styleSettled: true,
  titleSettings: ""
};

const stripFormat = (name: string, format: string | null | undefined) =>
  format && name.toLowerCase().endsWith(`.${format.toLowerCase()}`)
    ? name.slice(0, -(format.length + 1))
    : name;

/**
 * The file's current name is still its upload name. Names are stored without
 * the format and `original_name` keeps it ("Passport.pdf" against
 * "Passport"), so the upload name loses the file's own format first. A file
 * renamed by hand, or whose name a person kept, counts as titled; one the
 * server sent without an upload name never had another.
 */
export const stillUploadName = (node: FsNode): boolean => {
  if (node.title_confirmed) return false;
  const format = node.format ?? node.extension;
  const current = stripFormat(node.title ?? node.name, format).trim();
  const original = node.original_name?.trim();
  if (!original) return true;
  return stripFormat(original, format).trim() === current;
};

const refOf = (node: FsNode): FileRef => ({
  id: node.id,
  name: node.title ?? node.name
});

const itemsOf = (sections: WorkspaceSection[], id: WorkspaceSection["id"]) =>
  sections.find((s) => s.id === id)?.items ?? [];

/** The folder the organizer leaves what it can't place in. */
const UNSORTED_FOLDER = "unsorted";

/**
 * Files an organize would still place: at the top level, or in the Unsorted
 * folder, except internal files, which belong there (they are never exhibits).
 */
const unsortedOf = (live: FsNode[], files: FsNode[]) => {
  const byId = new Map(live.map((n) => [n.id, n]));
  const top = (node: FsNode): FsNode | null => {
    const seen = new Set<string>();
    let cur = node;
    while (cur.parent_id && byId.has(cur.parent_id) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.parent_id) as FsNode;
    }
    return cur === node ? null : cur;
  };
  const root: FsNode[] = [];
  const parked: FsNode[] = [];
  files.forEach((n) => {
    if (n.parent_id === null) {
      root.push(n);
      return;
    }
    const head = top(n);
    if (
      head &&
      (head.title ?? head.name ?? "").trim().toLowerCase() ===
        UNSORTED_FOLDER &&
      n.document_role !== "internal"
    )
      parked.push(n);
  });
  return { root, parked };
};

export const workflowFacts = (
  nodes: FsNode[],
  sections: WorkspaceSection[],
  caseFacts: CaseFacts = NO_CASE_FACTS
): WorkflowFacts => {
  const live = nodes.filter((n) => !n.is_deleted);
  const files = live.filter((n) => n.kind === "file");
  const { root, parked } = unsortedOf(live, files);
  return {
    files: files.length,
    unsorted: root.length + parked.length,
    folders: live.filter((n) => n.kind === "folder").length,
    allFiles: files.map(refOf),
    untitled: files.filter(stillUploadName).map(refOf),
    addedAt: files.flatMap((n) => (n.created_at ? [n.created_at] : [])),
    stamps: files.map((n) => ({
      ...refOf(n),
      addedAt: n.created_at ?? null,
      changedAt: n.updated_at ?? n.created_at ?? null,
      placedBy: n.placed_by ?? null
    })),
    unsortedFiles: [...root, ...parked].map(refOf),
    inUnsortedFolder: parked.length > 0,
    research: itemsOf(sections, "research"),
    letters: itemsOf(sections, "drafts"),
    forms: itemsOf(sections, "forms"),
    exhibitLists: itemsOf(sections, "exhibit-list"),
    packet: itemsOf(sections, "packet")[0] ?? null,
    packetParts: applyPacketOrder(
      packetPartsOf(
        itemsOf(sections, "drafts"),
        itemsOf(sections, "forms"),
        itemsOf(sections, "exhibit-list"),
        nodes,
        caseFacts.packetKindOrder,
        caseFacts.packetContents
      ),
      caseFacts.packetOrder ?? []
    ),
    packetPicks: {},
    packetOrder: [],
    packetContents: DEFAULT_PACKET_CONTENTS,
    packetKindOrder: DEFAULT_PACKET_ORDER,
    researchTopics: null,
    titleSettings: "",
    letterTypes: null,
    ...caseFacts
  };
};

const count = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const newest = (items: WorkspaceItem[]): string =>
  items.reduce(
    (max, i) => ((i.updatedAt ?? "") > max ? i.updatedAt ?? "" : max),
    ""
  );

type Records = Partial<Record<WorkflowId, WorkflowRunRecord>>;

const view = (
  id: WorkflowId,
  status: WorkflowStatus,
  detail: string,
  more: Pick<WorkflowView, "reasons" | "action" | "opens"> = {}
): WorkflowView => ({ id, status, detail, ...more });

const MAX_NAMED = 2;

/** "Passport.pdf, CV.pdf +3 more". */
export const namesOf = (refs: readonly FileRef[]): string => {
  const rest = refs.length - MAX_NAMED;
  return `${refs
    .slice(0, MAX_NAMED)
    .map((r) => r.name)
    .join(", ")}${rest > 0 ? ` +${rest} more` : ""}`;
};

const after = (
  at: string | null | undefined,
  since: string | null | undefined
) => !!at && !!since && Date.parse(at) > Date.parse(since);

/**
 * Files uploaded after the last successful organize. Organize's own moves
 * bump `updated_at` (and can leave a run record from before apply finished),
 * so only `created_at` counts, and a file the organizer already placed is
 * not a new upload.
 */
export const filesAddedSinceOrganize = (
  stamps: readonly FileStamp[],
  organizedAt: string | null | undefined
): FileStamp[] =>
  stamps.filter(
    (f) => f.placedBy !== "organizer" && after(f.addedAt, organizedAt)
  );

/** "Offer Letter.pdf was added", "3 files were added". */
const addedPhrase = (files: readonly FileRef[]) =>
  files.length === 1
    ? `${files[0].name} was added`
    : `${files.length} files were added`;

/** Why an item is out of date or failed: "Petition Letter: written before CV.pdf was added". */
const itemReason = (
  item: WorkspaceItem,
  stamps: readonly FileStamp[],
  verb: string
): string => {
  const { title } = item.artifact;
  if (item.status === "failed") {
    const why = item.detail?.trim();
    return why ? `${title} failed: ${why}` : `${title} failed`;
  }
  const added = stamps.filter((f) => after(f.addedAt, item.updatedAt));
  return added.length
    ? `${title}: ${verb} before ${addedPhrase(added)}`
    : `${title}: out of date`;
};

/** Items with work in them; an empty placeholder (idle) is no work yet. */
const WORKED = new Set<WorkspaceItem["status"]>([
  "ready",
  "stale",
  "failed",
  "running",
  "waiting"
]);

/**
 * A set of workspace items, read as one status. Done needs written items:
 * the placeholders a case is created with don't count.
 */
const fromItems = (
  id: WorkflowId,
  items: WorkspaceItem[],
  noun: string,
  empty: string,
  stamps: readonly FileStamp[] = [],
  verb = "written"
): WorkflowView => {
  const reasonsFor = (status: WorkspaceItem["status"]) =>
    items
      .filter((i) => i.status === status)
      .map((i) => itemReason(i, stamps, verb));
  const busy = items.filter(
    (i) => i.status === "running" || i.status === "waiting"
  );
  if (busy.length)
    return view(id, "running", `Writing ${count(busy.length, noun)}`);
  if (items.some((i) => i.status === "failed"))
    return view(
      id,
      "failed",
      `${count(
        items.filter((i) => i.status === "failed").length,
        noun
      )} failed`,
      { reasons: reasonsFor("failed") }
    );
  if (items.some((i) => i.status === "stale"))
    return view(
      id,
      "stale",
      `${count(
        items.filter((i) => i.status === "stale").length,
        noun
      )} out of date`,
      { reasons: reasonsFor("stale") }
    );
  const worked = items.filter((i) => WORKED.has(i.status));
  if (worked.length) return view(id, "done", count(worked.length, noun));
  return view(id, "ready", empty);
};

/** The checked letters with no written draft yet. */
const lettersMissing = (
  types: readonly LetterType[],
  letters: readonly WorkspaceItem[]
): LetterType[] =>
  types.filter(
    (t) =>
      !letters.some(
        (l) =>
          l.draftType === t.draftType &&
          (l.status === "ready" || l.status === "stale")
      )
  );

/** Where a step stands from the case itself, before this run's live state. */
export const derived = (
  id: WorkflowId,
  facts: WorkflowFacts,
  records: Records
): WorkflowView => {
  switch (id) {
    case "organize": {
      const last = records.organize;
      if (last?.status === "done") {
        const added = filesAddedSinceOrganize(facts.stamps, last.at);
        if (added.length)
          return view(
            id,
            "stale",
            `${count(added.length, "file")} added since organizing`,
            { reasons: [`Added since organizing: ${namesOf(added)}`] }
          );
      }
      if (facts.unsorted > 0) {
        const where = facts.inUnsortedFolder ? "not sorted" : "not in a folder";
        return view(
          id,
          "ready",
          `${count(facts.unsorted, "file")} ${where}`,
          facts.unsortedFiles.length
            ? {
                reasons: [
                  `${where[0].toUpperCase()}${where.slice(1)}: ${namesOf(
                    facts.unsortedFiles
                  )}`
                ]
              }
            : {}
        );
      }
      return view(
        id,
        "done",
        `${count(facts.files, "file")} in ${count(facts.folders, "folder")}`
      );
    }
    case "insights": {
      const last = records.insights;
      const added = filesAddedSinceOrganize(facts.stamps, last?.at);
      if (last?.status === "done" && !last.questions?.length && !added.length)
        return view(id, "done", "Nothing new to review");
      if (last?.status === "done" && added.length)
        return view(
          id,
          "stale",
          `${count(added.length, "file")} added since the review`,
          { reasons: [`Added since the review: ${namesOf(added)}`] }
        );
      return view(
        id,
        "ready",
        "Propose values from the files for you to confirm"
      );
    }
    case "inconsistencies": {
      const last = records.inconsistencies;
      const added = filesAddedSinceOrganize(facts.stamps, last?.at);
      if (last?.status === "done" && !last.questions?.length && !added.length)
        return view(id, "done", "Nothing inconsistent");
      if (last?.status === "done" && added.length)
        return view(
          id,
          "stale",
          `${count(added.length, "file")} added since the check`,
          { reasons: [`Added since the check: ${namesOf(added)}`] }
        );
      return view(
        id,
        "ready",
        "Find facts that disagree and leave a checklist"
      );
    }
    case "retitle": {
      const left = facts.untitled.length;
      const all = count(facts.files, "file");
      if (retitleCurrent(facts, records.retitle))
        return view(id, "done", `All ${all} retitled`);
      if (!left) return view(id, "done", "Every file has a title");
      const more = {
        reasons: [`Still named as uploaded: ${namesOf(facts.untitled)}`],
        action: `Retitle All ${count(facts.files, "File")}`
      };
      if (left < facts.files)
        return view(
          id,
          "stale",
          `${count(left, "file")} still ${
            left === 1 ? "has its" : "have their"
          } upload name`,
          more
        );
      return view(id, "ready", `Retitles all ${all}`, more);
    }
    case "number":
      return facts.styleSettled
        ? view(id, "done", facts.labelStyle)
        : view(
            id,
            "ready",
            `${facts.labelStyle} · The firm style applies on the next run`
          );
    case "exhibitList": {
      if (derived("number", facts, records).status !== "done")
        return view(id, "waiting", "Needs exhibit numbers first", {
          reasons: ["Waiting on Assign Exhibit Numbers"]
        });
      const v = fromItems(
        id,
        facts.exhibitLists,
        "list",
        "No exhibit list yet",
        facts.stamps,
        "drafted"
      );
      if (v.status === "running") return { ...v, detail: "Drafting" };
      return v.status === "done"
        ? { ...v, detail: "Ready to review in Exhibit List" }
        : v;
    }
    case "research": {
      const t = facts.researchTopics;
      if (t?.kind === "unavailable")
        return view(
          id,
          "unavailable",
          "Research isn't part of the firm's plan"
        );
      if (t?.kind === "ready") {
        const r = researchView(t);
        return view(id, r.status, r.detail, {
          reasons: r.reasons,
          opens: r.opens
        });
      }
      const v = fromItems(
        id,
        facts.research,
        "memo",
        "No memos yet",
        facts.stamps,
        "researched"
      );
      return v.status === "running" ? { ...v, detail: "Researching" } : v;
    }
    case "letters": {
      const v = fromItems(
        id,
        facts.letters,
        "draft",
        "No letters yet",
        facts.stamps
      );
      if (v.status !== "done" && v.status !== "ready") return v;
      const missing = lettersMissing(facts.letterTypes ?? [], facts.letters);
      if (missing.length) {
        const total = facts.letterTypes?.length ?? 0;
        const written = total - missing.length;
        return view(
          id,
          "ready",
          written
            ? `${written} of ${count(total, "letter")} written`
            : missing.length === 1
            ? `${missing[0].label} not written yet`
            : `${count(missing.length, "letter")} not written yet`,
          { reasons: missing.map((t) => `${t.label}: not written yet`) }
        );
      }
      if (v.status !== "done") return v;
      const last = records.organize;
      const changed =
        last?.status === "done" && last.at > newest(facts.letters);
      return changed
        ? view(id, "stale", "Exhibits changed since drafting", {
            reasons: facts.letters
              .filter((l) => last.at > (l.updatedAt ?? ""))
              .map(
                (l) =>
                  `${l.artifact.title}: written before the exhibits were reorganized`
              )
          })
        : v;
    }
    case "forms": {
      const v = formsView(facts.forms);
      return view(
        id,
        v.status,
        v.detail,
        v.reasons ? { reasons: v.reasons } : {}
      );
    }
    case "packet": {
      const p = facts.packet;
      if (p?.status === "running") return view(id, "running", "Building");
      if (p?.status === "failed")
        return view(id, "failed", p.detail ?? "The last build failed");
      const forms = p ? packetForms(facts.forms, p) : null;
      const leftOut = forms?.leftOut ? [forms.leftOut] : [];
      if (p?.status === "stale" || (p && forms?.filledAfter.length)) {
        const changed = facts.stamps.filter(
          (f) =>
            after(f.addedAt, p.updatedAt) || after(f.changedAt, p.updatedAt)
        );
        const files =
          p.status !== "stale"
            ? []
            : [
                changed.length
                  ? `Changed since it was built: ${namesOf(changed)}`
                  : "Files or exhibit settings changed since it was built"
              ];
        return view(id, "stale", "Out of date", {
          reasons: [...(forms?.filledAfter ?? []), ...files, ...leftOut]
        });
      }
      if (p?.status === "ready")
        return view(
          id,
          "done",
          [
            p.detail ?? "Packet built",
            forms?.leftOutCount
              ? `${count(forms.leftOutCount, "form")} left out`
              : null
          ]
            .filter(Boolean)
            .join(" · "),
          leftOut.length ? { reasons: leftOut } : {}
        );
      if (facts.untitled.length > 0)
        return view(id, "waiting", "Needs exhibit titles first", {
          reasons: [`Still named as uploaded: ${namesOf(facts.untitled)}`]
        });
      return view(id, "ready", "No packet yet");
    }
    default:
      return view(id, "ready", "");
  }
};

/** A live step held by a question or approval: what it waits on, and the way out. */
const heldView = (
  id: WorkflowId,
  status: WorkflowStatus,
  hold: WorkflowHold
): WorkflowView => {
  const text = holdText(hold);
  return {
    ...view(id, status, text.detail, { reasons: text.reasons }),
    live: true,
    ...(hold.kind === "turn"
      ? {}
      : {
          opens: "thread" as const,
          threadId: hold.threadId,
          openLabel: text.openLabel
        })
  };
};

const stepView = (
  id: WorkflowId,
  facts: WorkflowFacts,
  records: Records,
  step: WorkflowStepState | undefined
): WorkflowView => {
  if (step?.status === "queued")
    return step.hold
      ? heldView(id, "waiting", step.hold)
      : view(id, "queued", step.detail ?? "Up next in this run");
  if (step?.status === "running")
    return view(id, "running", step.detail ?? "Casey is working");
  if (step?.status === "needs-you")
    return step.hold
      ? heldView(id, "needs-you", step.hold)
      : {
          ...view(id, "needs-you", step.detail ?? "Casey is waiting on you", {
            reasons: ["Open the chat to see what Casey needs."]
          }),
          live: true,
          ...(step.threadId
            ? {
                opens: "thread",
                threadId: step.threadId,
                openLabel: "Open Chat"
              }
            : {})
        };
  if (facts.files === 0 && id !== "number")
    return view(id, "needs-files", "Upload documents first");
  const record = records[id];
  if (record?.status === "failed")
    return view(id, "failed", record.detail ?? "The last run failed");
  if (record?.upToDate) {
    const own = derived(id, facts, records);
    return own.status === "done" && record.detail
      ? { ...own, detail: record.detail }
      : own;
  }
  if (record?.status === "skipped")
    return view(id, "skipped", record.detail ?? "Skipped");
  if (record?.status === "done" && record.questions?.length)
    return {
      ...view(
        id,
        "needs-you",
        `Casey left ${count(record.questions.length, "question")}`,
        { reasons: record.questions }
      ),
      ...(record.threadId
        ? {
            opens: "thread",
            threadId: record.threadId,
            openLabel: "Answer in Chat"
          }
        : {})
    };
  return derived(id, facts, records);
};

/** Every workflow's card state, in catalog order. */
export const workflowViews = (
  facts: WorkflowFacts,
  records: Records,
  steps: Partial<Record<WorkflowId, WorkflowStepState>>
): WorkflowView[] =>
  WORKFLOWS.map(({ id }) => {
    const out = stepView(id, facts, records, steps[id]);
    const model = records[id]?.model;
    return model && out.status !== "running" ? { ...out, model } : out;
  });

const RUNNABLE: ReadonlySet<WorkflowStatus> = new Set([
  "ready",
  "stale",
  "failed",
  "waiting",
  "skipped"
]);

/** The steps progress counts and runs pick up: optional ones are left out. */
export const requiredViews = <T extends Pick<WorkflowView, "id">>(
  views: readonly T[]
): T[] => views.filter((v) => !isOptional(v.id));

/** What Run Remaining runs: every required step not done, running or blocked on files. */
export const remainingIds = (views: WorkflowView[]): WorkflowId[] =>
  requiredViews(views)
    .filter((v) => RUNNABLE.has(v.status))
    .map((v) => v.id);

/** The step to do next: something out of date or failed, else the first ready one. */
export const nextUpId = (views: WorkflowView[]): WorkflowId | null => {
  const required = requiredViews(views);
  return (
    (
      required.find((v) => v.status === "stale" || v.status === "failed") ??
      required.find((v) => v.status === "ready") ??
      required.find((v) => RUNNABLE.has(v.status)) ??
      null
    )?.id ?? null
  );
};

/** Required steps done; an optional one never counts. */
export const doneCount = (views: WorkflowView[]): number =>
  requiredViews(views).filter((v) => v.status === "done").length;

/**
 * Exhibit numbering is preselected: it reads Done as soon as the case has a
 * label style, and a run only applies that style. It never counts as work
 * the attorney has started, nor as something a start-over would replace.
 */
const PRESELECTED: ReadonlySet<WorkflowId> = new Set(["number"]);

/** Some step besides the preselected numbering is done. */
export const hasStartedWork = (views: readonly WorkflowView[]): boolean =>
  views.some((v) => v.status === "done" && !PRESELECTED.has(v.id));

/**
 * Drafts, forms and the packet a start-over would regenerate: only ones with
 * content. A form a new case starts with, or a draft stub, is still blank and
 * loses nothing.
 */
export const writtenItems = (
  items: readonly WorkspaceItem[]
): WorkspaceItem[] =>
  items.filter((i) => i.status === "ready" || i.status === "stale");

/** The case already has work a start-over would replace. */
export const hasPriorWork = (facts: WorkflowFacts, records: Records): boolean =>
  facts.folders > 0 ||
  facts.untitled.length < facts.files ||
  writtenItems(facts.letters).length > 0 ||
  writtenItems(facts.forms).length > 0 ||
  writtenItems(facts.packet ? [facts.packet] : []).length > 0 ||
  Object.entries(records).some(
    ([id, r]) => !PRESELECTED.has(id as WorkflowId) && r?.status === "done"
  );

/**
 * Gray until done or in trouble: green when done, orange when it needs an
 * update or an answer, red when it failed. Running is gray with motion.
 */
export type StatusTone = "neutral" | "success" | "orange" | "danger";

/**
 * Light's `warn` reads brown, and dark's `orange` reads coral; the orange
 * tone is `orange` in light and `warn` in dark.
 */
export const ORANGE_DARK = "dark:bg-warn-soft dark:text-warn";

export type StatusMeta = {
  label: string;
  tone: StatusTone;
  /** The Run button's label; null when it can't run now. */
  action: string | null;
};

export const STATUS_META: Record<WorkflowStatus, StatusMeta> = {
  "needs-files": { label: "Needs Files", tone: "neutral", action: null },
  ready: { label: "Ready", tone: "neutral", action: "Run" },
  queued: { label: "Queued", tone: "neutral", action: null },
  running: { label: "Running", tone: "neutral", action: null },
  "needs-you": { label: "Needs You", tone: "orange", action: null },
  done: { label: "Done", tone: "success", action: "Run Again" },
  stale: { label: "Out of Date", tone: "orange", action: "Update" },
  waiting: { label: "Waiting", tone: "neutral", action: "Run" },
  failed: { label: "Failed", tone: "danger", action: "Retry" },
  skipped: { label: "Skipped", tone: "neutral", action: "Run" },
  unavailable: { label: "Not in Plan", tone: "neutral", action: null }
};
